import { lazy } from "react";

// Marks an error as having come out of a dynamic-import evaluation, i.e. a
// genuine broken / stale chunk. Only lazyWithRetry can set this, because only
// it sits on the import promise.
//
// WHY THIS EXISTS: ChunkErrorBoundary's short-identifier TDZ pattern
// ("Cannot access 'z' before initialization") was matching a REAL crash in
// current code, not just a stale bundle. Every minified binding in the
// production bundle is 1-3 characters, so any render-time Temporal Dead Zone
// anywhere in the SPA was being presented to the user as
// "New Easter Eggs Have Been Added — Please Refresh", whose only button
// re-runs the same crash forever. A render-time TDZ goes straight to the error
// boundary via getDerivedStateFromError and never touches the import promise,
// so the marker cleanly separates the two cases: tagged = stale chunk,
// untagged = a real bug that deserves the honest "Something went wrong".
export const LAZY_CHUNK_FAILURE_FLAG = "__kaiLazyChunkFailure";

export function markLazyChunkFailure(error) {
  if (!error || typeof error !== "object") return error;
  try {
    error[LAZY_CHUNK_FAILURE_FLAG] = true;
  } catch (e) {
    // A frozen/sealed error cannot carry the marker; the boundary then just
    // treats it as a non-chunk error, which is the safe direction.
  }
  return error;
}

export function isLazyChunkFailure(error) {
  return Boolean(error && typeof error === "object" && error[LAZY_CHUNK_FAILURE_FLAG]);
}

/**
 * Does this error look like a broken / stale chunk?
 *
 * Only ever call this from a dynamic-import rejection. The short-identifier TDZ
 * pattern below cannot tell a stale chunk from a render-time crash by itself —
 * every minified identifier is 1-3 characters — so on its own it is not
 * evidence of a chunk problem. Here it IS evidence, because the caller is
 * standing on the import promise and a broken chunk is the only way a module
 * can be evaluated at all. ChunkErrorBoundary gets the same error object taken
 * off a render throw instead, so it may not use this directly; it checks the
 * marker this module sets at the moment it rejects.
 */
export function isStaleChunkError(error) {
  const msg = (error?.message || "").toString();
  return (
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("Unexpected token '<'") ||
    /^Cannot access '\w{1,3}' before initialization$/.test(msg) ||
    error?.name === "ChunkLoadError"
  );
}

/**
 * Lazy load a component with automatic retry on chunk loading failure
 *
 * @param {Function} importFunc - Dynamic import function
 * @param {number} retries - Number of retry attempts (default: 3)
 * @param {number} interval - Delay between retries in ms (default: 1500)
 * @returns {React.LazyExoticComponent}
 *
 * Common causes of chunk loading failures:
 * - Network interruptions
 * - Browser cache issues after deployment
 * - CDN/server temporary unavailability
 * - Webpack chunk hash mismatches
 */
export function lazyWithRetry(importFunc, retries = 3, interval = 1500) {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      const attemptImport = (attemptsLeft) => {
        importFunc()
          .then(resolve)
          .catch((error) => {
            // If we have retries left, try again after a delay
            if (attemptsLeft > 0) {
              console.warn(
                `Chunk loading failed. Retrying... (${retries - attemptsLeft + 1}/${retries})`,
                error
              );

              setTimeout(() => {
                attemptImport(attemptsLeft - 1);
              }, interval);
            } else {
              console.error("Chunk loading failed after all retries:", error);
              // If this looks like a stale-chunk error (post-deploy hash
              // mismatch) AND we haven't already auto-reloaded this minute,
              // do a cache-busting navigation so the user gets fresh HTML
              // without having to manually hard-refresh. The _r param is
              // minute-granular, so a single reload per minute is the worst
              // case if chunks genuinely keep failing (e.g. broken deploy).
              const isStaleChunk = isStaleChunkError(error);
              const alreadyReloaded = new URL(window.location.href).searchParams.has("_r");
              if (isStaleChunk && !alreadyReloaded) {
                const url = new URL(window.location.href);
                url.searchParams.set("_r", String(Math.round(Date.now() / 60000)));
                window.location.replace(url.toString());
                // Don't reject — the page is navigating away.
                return;
              }
              // About to hand this to ChunkErrorBoundary. Tag it when it really
              // did come out of a chunk import, so the boundary can tell this
              // apart from a same-shaped TDZ thrown during render by current
              // code (which is a bug, not a stale bundle).
              if (isStaleChunk) {
                markLazyChunkFailure(error);
              }
              // Not a stale-chunk error, or we already reloaded — let
              // ChunkErrorBoundary render the "Update Available" modal.
              reject(error);
            }
          });
      };

      attemptImport(retries - 1);
    });
  });
}

/**
 * Preload a lazy-loaded component
 * Useful for prefetching chunks before they're needed
 *
 * @param {React.LazyExoticComponent} lazyComponent - Lazy component to preload
 */
export function preloadComponent(lazyComponent) {
  // Access the internal preload function if available
  if (lazyComponent && typeof lazyComponent._init === 'function') {
    lazyComponent._init(lazyComponent._payload);
  }
}
